import { useEffect, useState } from "react";
import { LayoutComponent } from "../LayoutComponent";
import {
  Cell,
  Column,
  Row,
  TableView,
  TableBody,
  TableHeader,
  Flex,
  Heading,
  Content,
  View,
  Link,
  ProgressCircle,
  DialogContainer,
  Dialog,
} from "@adobe/react-spectrum";
import moment from "moment";
import { formatDecimalToString } from "../../../../utils/helperUtil";
import ProjectProductAddEdit, { AttributeData } from "./ProjectProductAddEdit";
import ProductSelectionModal, {
  SelectedProduct,
} from "./ProductSelectionModal";
import { useDependency } from "../../../../contexts/DependencyProvider";
import { ListProjectItemRequest } from "../../../../services/soap/item/requests/ListProjectItemRequest";
import { ListProjectItemRequestOptions_ProjectItem } from "../../../../services/soap/item/requests/ListProjectItemRequestOptions";
import { useViewInfo } from "../../../../hooks/UseViewInfo";
import { useTranslation } from "react-i18next";
import { FPAData, FPADataTypes } from "../../../../infra/protected/FPA/FPAData";
import { UpdateProjectItemRequest } from "../../../../services/soap/item/requests/UpdateProjectItemRequest";
import { generateProjectProductItem } from "../../../../services/soap/item/requests/CreateProjectItemOptions";
import { ToastQueue } from "@react-spectrum/toast";
import { GetItemRequest } from "../../../../services/soap/item/requests/GetItemRequest";
import { CreateProjectItemRequest } from "../../../../services/soap/item/requests/CreateProjectItemRequest";
import { DeleteProjectItemRequest } from "../../../../services/soap/item/requests/DeleteProjectItemRequest";
import { useTableViewHeight } from "../../../../utils/tableViewUtils";
import styles from "./project_product_component.module.css";
import { GetItemTypeRequest } from "../../../../services/soap/item/requests/GetItemTypeRequest";
import { GetCodebookRequest } from "../../../../services/soap/item/requests/GetCodebookRequest";
import { GetItemTypeResponse } from "../../../../services/soap/item/responses/GetItemTypeResponse";
import { GetCodebookResponse } from "../../../../services/soap/item/responses/GetCodebookResponse";

interface ProjectProductProps {
  attrib0ColumnName?: string;
  attrib1ColumnName?: string;
  attrib2ColumnName?: string;
  attrib0ColumnVisible?: boolean;
  attrib1ColumnVisible?: boolean;
  attrib2ColumnVisible?: boolean;
  billingDateColumnName?: string;
  billingDateColumnVisible?: boolean;
  componentTitle?: string;
  minHeight?: number;
  percentWidth?: number;
  productType?: number;
  quantityColumnName?: string;
  quantityColumnVisible?: boolean;
  serialNumberColumnName?: string;
  serialNumberColumnVisible?: boolean;
  serialNumberComponentIdent?: string;
  unitPriceColumnName?: string;
  unitPriceColumnVisible?: boolean;
  supplierColumnVisible?: boolean;
  selectedItem?: FPAData;
  isNew?: boolean;
  setData?: (dataItems: any[]) => void;
  isRequired?: boolean; // need for Create New Context
}

export interface RowData {
  id: string;
  quantity: number;
  partID: string;
  _refId?: string;
  supplier?: string;
  supplierName?: string;
  partNumber: string;
  productName: string;
  currency: string;
  unitPrice: number;
  amountWithVat: number;
  vatValue: number;
  totalPrice: string;
  totalPriceWithVat: string;
  attrib0: string;
  attrib0_id: string;
  attrib1: string;
  attrib1_id: string;
  attrib2: string;
  attrib2_id: string;
  refId: string;
  totalQuantity: number;
  amount: number;
  billingDate: string;
  itemType?: string;
}

function ProjectProduct({
  attrib0ColumnName = "Col.0",
  attrib1ColumnName = "Col.1",
  attrib2ColumnName = "Col.2",
  attrib0ColumnVisible = false,
  attrib1ColumnVisible = false,
  attrib2ColumnVisible = false,
  billingDateColumnName = "Billing Date",
  billingDateColumnVisible = false,
  componentTitle = "Project products",
  minHeight = 64,
  percentWidth = 100,
  productType = 2,
  quantityColumnName = "Quantity",
  quantityColumnVisible = false,
  serialNumberColumnName = "Serial Number",
  serialNumberColumnVisible = false,
  serialNumberComponentIdent = "",
  unitPriceColumnName = "Unit Price",
  unitPriceColumnVisible = false,
  supplierColumnVisible = false,
  selectedItem,
  isNew,
  setData,
  isRequired,
}: ProjectProductProps) {
  const { t } = useTranslation();

  let defaultRowData: RowData = {
    id: "0",
    quantity: 1,
    partID: "",
    _refId: "",
    partNumber: "",
    productName: "",
    currency: "",
    unitPrice: 0,
    amountWithVat: 0,
    vatValue: 0,
    totalPrice: "0",
    totalPriceWithVat: "0",
    attrib0: "",
    attrib0_id: "",
    attrib1: "",
    attrib1_id: "",
    attrib2: "",
    attrib2_id: "",
    refId: "",
    totalQuantity: 0,
    amount: 0,
    supplier: "",
    supplierName: "",
    billingDate: moment().toISOString(),
  };

  const { itemService, store } = useDependency();
  const { isMobile } = useViewInfo();
  const [projectProductData, setProjectProductData] = useState<any>([]);
  const [rowsData, setRowsData] = useState<any>([]);
  const [selectedRow, setSelectedRow] = useState<RowData>(defaultRowData);
  const [showLoader, setShowLoader] = useState<boolean>(true);
  const [showAddEditRow, setShowAddEditRow] = useState<boolean>(false);
  const [selectedKeys, setSelectedKeys] = useState<Set<any>>(new Set());
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [attributesCache, setAttributesCache] = useState<
    Record<string, AttributeData[]>
  >({});
  const [activeAttributesData, setActiveAttributesData] = useState<
    AttributeData[]
  >([]);
  const [isProductSelectionModalOpen, setIsProductSelectionModalOpen] =
    useState<boolean>(false);

  // Use the utility for dynamic height calculations
  const { tableHeight, containerHeight, tableStyles } = useTableViewHeight(
    rowsData.length,
    {
      headerHeight: 50,
      rowHeight: 50,
      maxVisibleRows: 5,
      containerExtraSpace: 0,
      maxContainerHeight: 550,
      showOverflow: true,
    },
  );

  const [columnsData, setColumnsData] = useState<any[]>([
    { name: "Ref Id", key: "partNumber", width: 120 },
    { name: "Product", key: "productName", width: 200 },
    { name: "Supplier", key: "supplierName", width: 200 },
    { name: attrib0ColumnName, key: "attrib0", width: 120 },
    { name: attrib1ColumnName, key: "attrib1", width: 120 },
    { name: attrib2ColumnName, key: "attrib2", width: 120 },
    { name: serialNumberColumnName, key: "refId" },
    { name: quantityColumnName, key: "quantity", width: 100 },
    { name: unitPriceColumnName, key: "unitPriceCurr", width: 150 },
    { name: billingDateColumnName, key: "billingDateFormatted", width: 120 },
  ]);

  const [columnsDataMobile, setColumnsDataMobile] = useState<any[]>([
    { name: "Ref Id", key: "partNumber" },
    { name: "Product", key: "productName" },
    { name: "Supplier", key: "supplierName" },
    { name: attrib0ColumnName, key: "attrib0" },
    { name: attrib1ColumnName, key: "attrib1" },
    { name: attrib2ColumnName, key: "attrib2" },
    { name: serialNumberColumnName, key: "refId" },
    { name: quantityColumnName, key: "quantity" },
    { name: unitPriceColumnName, key: "unitPriceCurr" },
    { name: billingDateColumnName, key: "billingDateFormatted", width: 100 },
  ]);

  useEffect(() => {
    // Define the columns to hide based on their visibility state
    const columnsToHide: any = [
      { key: "supplierName", visible: supplierColumnVisible },
      { key: "attrib0", visible: attrib0ColumnVisible },
      { key: "attrib1", visible: attrib1ColumnVisible },
      { key: "attrib2", visible: attrib2ColumnVisible },
      { key: "refId", visible: serialNumberColumnVisible },
      { key: "quantity", visible: quantityColumnVisible },
      { key: "unitPriceCurr", visible: unitPriceColumnVisible },
      { key: "billingDateFormatted", visible: billingDateColumnVisible },
    ];

    // Filter the columnsData array to exclude columns that are not visible
    const filteredColumnsData = columnsData.filter((column) =>
      columnsToHide.every((col: any) => col.visible || col.key !== column.key),
    );

    // Filter the columnsDataMobile array to exclude columns that are not visible
    const filteredColumnsDataMobile = columnsDataMobile.filter((column) =>
      columnsToHide.every((col: any) => col.visible || col.key !== column.key),
    );

    // Update the state with the filtered columns data
    setColumnsData(filteredColumnsData);
    setColumnsDataMobile(filteredColumnsDataMobile);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    // Dependencies: the effect will run whenever any of these visibility states change
    attrib0ColumnVisible,
    attrib1ColumnVisible,
    attrib2ColumnVisible,
    serialNumberColumnVisible,
    quantityColumnVisible,
    unitPriceColumnVisible,
    billingDateColumnVisible,
    supplierColumnVisible,
  ]);

  useEffect(() => {
    setShowAddEditRow(false);
    (async () => {
      setShowLoader(true);
      !isNew && (await loadProjectProductData());
      setShowLoader(false);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedItem, productType]);

  const updateProjectProductData = async (projectProductData: any[]) => {
    if (!projectProductData) return [];
    return Promise.all(
      projectProductData.map(async (item) => {
        const partNumber = await getPartNumber(item);
        return {
          ...item,
          _refId: `${partNumber}_${item.productName}_ref`,
          partNumber,
        };
      }),
    );
  };

  const setRecordIds = (record: any, selectedItem: any) => {
    switch (selectedItem?.type) {
      case FPADataTypes.ACTIVITY:
        record.folderId = selectedItem?.parent?.parent_id;
        record.projectId = String(selectedItem?.parent_id);
        record.activityId = String(selectedItem?.id);
        break;
      case FPADataTypes.PROJECT:
        record.folderId = "";
        record.projectId = String(selectedItem?.id);
        record.activityId = "";
        break;
      case FPADataTypes.FOLDER:
        record.folderId = String(selectedItem?.id);
        record.projectId = "";
        record.activityId = "";
        break;
    }
  };

  const loadAttributesData = async (
    item_type: string,
  ): Promise<AttributeData[]> => {
    let itemTypeRes: GetItemTypeResponse = await itemService.getItemType(
      new GetItemTypeRequest(store.Server, store.SessionId, item_type),
    );
    let codebookRes: GetCodebookResponse | null = null;

    var attrib0: AttributeData = {
      filter: itemTypeRes.ITEM_TYPE.attrib0_filter,
      name: itemTypeRes.ITEM_TYPE.attrib0_name,
      priority: itemTypeRes.ITEM_TYPE.attrib0_priority,
      type: itemTypeRes.ITEM_TYPE.attrib0_type,
      values_name: itemTypeRes.ITEM_TYPE.attrib0_values_name,
      values: itemTypeRes.ITEM_TYPE.attrib0_values,
    };

    var attrib1: AttributeData = {
      filter: itemTypeRes.ITEM_TYPE.attrib1_filter,
      name: itemTypeRes.ITEM_TYPE.attrib1_name,
      priority: itemTypeRes.ITEM_TYPE.attrib1_priority,
      type: itemTypeRes.ITEM_TYPE.attrib1_type,
      values_name: itemTypeRes.ITEM_TYPE.attrib1_values_name,
      values: itemTypeRes.ITEM_TYPE.attrib1_values,
    };

    var attrib2: AttributeData = {
      filter: itemTypeRes.ITEM_TYPE.attrib2_filter,
      name: itemTypeRes.ITEM_TYPE.attrib2_name,
      priority: itemTypeRes.ITEM_TYPE.attrib2_priority,
      type: itemTypeRes.ITEM_TYPE.attrib2_type,
      values_name: itemTypeRes.ITEM_TYPE.attrib2_values_name,
      values: itemTypeRes.ITEM_TYPE.attrib2_values,
    };

    if (attrib0.type === 5 && attrib0.values) {
      codebookRes = await itemService.getCodebook(
        new GetCodebookRequest(store.Server, store.SessionId, attrib0.values),
      );
      attrib0.codebooks = codebookRes.CODEBOOK[0].VALUES.map((item: any) => {
        return {
          id: item.id,
          name: item.name,
          priority: item.priority,
        };
      });
    }

    if (attrib1.type === 5 && attrib1.values) {
      codebookRes = await itemService.getCodebook(
        new GetCodebookRequest(store.Server, store.SessionId, attrib1.values),
      );
      attrib1.codebooks = codebookRes.CODEBOOK[0].VALUES.map((item: any) => {
        return {
          id: item.id,
          name: item.name,
          priority: item.priority,
        };
      });
    }

    if (attrib2.type === 5 && attrib2.values) {
      codebookRes = await itemService.getCodebook(
        new GetCodebookRequest(store.Server, store.SessionId, attrib2.values),
      );
      attrib2.codebooks = codebookRes.CODEBOOK[0].VALUES.map((item: any) => {
        return {
          id: item.id,
          name: item.name,
          priority: item.priority,
        };
      });
    }
    // console.log('attrib0', attrib0);
    // console.log('attrib1', attrib1);
    // console.log('attrib2', attrib2);

    return [attrib0, attrib1, attrib2];
  };

  const ensureAttributesForType = async (
    itemType: string,
  ): Promise<AttributeData[]> => {
    if (!itemType) {
      return [];
    }

    const normalizedType = String(itemType);

    if (attributesCache[normalizedType]) {
      setActiveAttributesData(attributesCache[normalizedType]);
      return attributesCache[normalizedType];
    }

    const attributes = await loadAttributesData(normalizedType);
    setAttributesCache((prev) => ({ ...prev, [normalizedType]: attributes }));
    setActiveAttributesData(attributes);
    return attributes;
  };

  const handleLoadAttributes = async (
    itemType: string,
  ): Promise<AttributeData[]> => {
    return ensureAttributesForType(itemType);
  };

  // Function to load project product data
  const loadProjectProductData = async () => {
    // Retrieve selected item
    //console.log('selectedItem', selectedItem);
    let record: any = {};
    setRecordIds(record, selectedItem);

    // Call API to fetch project product data
    let projecrProdRes: any = await itemService.listProjectItem(
      new ListProjectItemRequest(
        store.Server,
        store.SessionId,
        50,
        0,
        new ListProjectItemRequestOptions_ProjectItem(
          record.folderId,
          record.projectId,
          record.activityId,
          String(productType),
        ),
      ),
    );

    console.log("projecrProdRes.ROWS", projecrProdRes.ROWS);

    const item_type = projecrProdRes.ROWS
      ? projecrProdRes.ROWS[0].ITEM.itemType
      : null;

    let attributes_data = item_type
      ? await ensureAttributesForType(item_type)
      : [];
    setActiveAttributesData(attributes_data);
    // Set project product data state
    setProjectProductData(projecrProdRes);

    // If API call is successful
    if (projecrProdRes?.result === "OK") {
      // Map the project product data to desired format
      let projectProductData = projecrProdRes?.ROWS?.map((item: any) => {
        return {
          id: item.ITEM.id,
          quantity: formatDecimalToString(
            Number.parseFloat(item.ITEM.quantity).toFixed(2),
          ),
          partID: item.ITEM.item,
          _refId: "",
          partNumber: "Part Number",
          productName: item.ITEM.name,
          supplier: item.ITEM.supplier,
          supplierName: item.ITEM.supplierName,
          currency: item.ITEM.currency,
          vatValue: item.ITEM.vatValue,
          amountWithVat: item.ITEM.amountWithVat,
          unitPrice: formatDecimalToString(item.ITEM.amount),
          unitPriceCurr:
            formatDecimalToString(item.ITEM.amount) + " " + item.ITEM.currency,
          totalPrice: formatDecimalToString(
            Number.parseFloat(item.ITEM.totalAmount).toFixed(2),
          ),
          totalPriceCurr:
            formatDecimalToString(
              Number.parseFloat(item.ITEM.totalAmount).toFixed(2),
            ) +
            " " +
            item.ITEM.currency,
          purchasePrice: item.ITEM.purchasePrice,
          totalAmountWithVat: item.ITEM.totalAmountWithVat,
          attrib0: item.ITEM.attrib0,
          attrib0_id: item.ITEM.attrib0_id,
          attrib1: item.ITEM.attrib1,
          attrib1_id: item.ITEM.attrib1_id,
          attrib2: item.ITEM.attrib2,
          attrib2_id: item.ITEM.attrib2_id,
          refId: item.ITEM.refId,
          billingDate: item.ITEM.billingDate,
          billingDateFormatted: moment(item.ITEM.billingDate).format(
            "MM/DD/YYYY",
          ),
          itemType: item.ITEM.itemType,
        };
      });

      // Update project product data with additional information
      updateProjectProductData(projectProductData).then((updatedData: any) => {
        console.log("updatedData", updatedData);
        setRowsData(updatedData);
        //console.log('rowsData', rowsData);
      });
    }
  };

  const getPartNumber = async (item: any) => {
    //console.log('item', item);
    let itemResp = await itemService.getItem(
      new GetItemRequest(store.Server, store.SessionId, item.partID),
    );
    //console.log('itemResp', itemResp);
    return itemResp.ITEM.refId;
  };

  const handleRowClick = async (rowId: any): Promise<void> => {
    const rowData: any = rowsData.find(
      (row: any) => row?.id === rowId.currentKey,
    );

    // Load attributes for this specific item's type
    if (rowData?.itemType) {
      await ensureAttributesForType(rowData.itemType);
    }

    setSelectedRow(rowData);
    setShowAddEditRow(true);
    isMobile && setIsOpen(true);
    setSelectedKeys(rowId);
  };

  const handleRowClose = (): void => {
    setSelectedRow(defaultRowData);
    setShowAddEditRow(false);
    isMobile && setIsOpen(false);
    handleUnselect();
  };

  const handleUnselect = () => {
    setSelectedKeys(new Set());
  };

  const handleSaveSelectedProducts = async (
    selectedProducts: SelectedProduct[],
  ) => {
    setShowLoader(true);
    try {
      let successCount = 0;
      let failureCount = 0;

      for (const product of selectedProducts) {
        console.log("product", product);
        try {
          let record: any = {};
          setRecordIds(record, selectedItem);

          // Save parent product
          const result = await itemService.createProjectItem(
            new CreateProjectItemRequest(
              store.Server,
              store.SessionId,
              generateProjectProductItem(
                record.folderId,
                record.projectId,
                record.activityId,
                product.partID,
                product.productName,
                product.quantity,
                product.unitPrice,
                product.unitPrice * product.quantity,
                product.amountWithVat * product.quantity,
                product.currency,
                String(product.vatValue),
                String(product.purchasePrice),
                "", // attrib0
                "", // attrib0_id
                "", // attrib1
                "", // attrib1_id
                "", // attrib2
                "", // attrib2_id
                "", // supplier
                product.partNumber,
                moment().toISOString(),
                "1",
                { type: String(productType) },
              ),
            ),
          );

          if (result.result === "OK") {
            successCount++;

            // Save component products if they exist
            if (product.components && product.components.length > 0) {
              for (const component of product.components) {
                try {
                  const compRecord: any = {};
                  setRecordIds(compRecord, selectedItem);

                  const compResult = await itemService.createProjectItem(
                    new CreateProjectItemRequest(
                      store.Server,
                      store.SessionId,
                      generateProjectProductItem(
                        compRecord.folderId,
                        compRecord.projectId,
                        compRecord.activityId,
                        component.component,
                        component.componentName,
                        parseFloat(component.countMin) || 1,
                        parseFloat(component.amount) || 0,
                        (parseFloat(component.amount) || 0) *
                          (parseFloat(component.countMin) || 1),
                        (parseFloat(component.amountWithVat) || 0) *
                          (parseFloat(component.countMin) || 1),
                        component.currencyName,
                        component.vatValue,
                        component.purchasePrice,
                        "", // attrib0
                        "", // attrib0_id
                        "", // attrib1
                        "", // attrib1_id
                        "", // attrib2
                        "", // attrib2_id
                        "", // supplier
                        "", // refId
                        moment().toISOString(),
                        "1",
                        { type: String(productType) },
                      ),
                    ),
                  );

                  if (compResult.result === "OK") {
                    successCount++;
                  } else {
                    failureCount++;
                  }
                } catch (compError) {
                  console.error("Error saving component:", compError);
                  failureCount++;
                }
              }
            }
          } else {
            failureCount++;
          }
        } catch (error) {
          console.error("Error saving product:", error);
          failureCount++;
        }
      }

      if (successCount > 0) {
        await loadProjectProductData();
        ToastQueue.positive(`Successfully added ${successCount} product(s)`, {
          timeout: 3000,
        });
      }

      if (failureCount > 0) {
        ToastQueue.negative(`Failed to add ${failureCount} product(s)`, {
          timeout: 3000,
        });
      }
    } catch (error: any) {
      ToastQueue.negative(error.message || "Failed to save products", {
        timeout: 3000,
      });
    } finally {
      setShowLoader(false);
    }
  };

  const addRecord = async (record: any) => {
    try {
      if (isNew && setData) {
        const updatedRowsData = {
          ...record,
          id: Math.random().toString(36).substring(7),
          unitPriceCurr:
            formatDecimalToString(record.unitPrice) + " " + record.currency,
          billingDateFormatted: moment(record.billingDate).format("MM/DD/YYYY"),
        };

        // Add the new record to the updated rows data
        const newRowsData = [...rowsData, updatedRowsData];
        // console.log('newRowsData', newRowsData);
        // Update the state with the new rows data
        setRowsData(newRowsData);
        setData(newRowsData);
        handleRowClose();
        return;
      }
      setShowLoader(true);
      //console.log('record', record);
      setRecordIds(record, selectedItem);

      let result = await itemService.createProjectItem(
        new CreateProjectItemRequest(
          store.Server,
          store.SessionId,
          generateProjectProductItem(
            record.folderId,
            record.projectId,
            record.activityId,
            record.partID,
            record.productName,
            record.quantity,
            record.unitPrice,
            record.totalPrice,
            record.totalAmountWithVat || 0,
            record.currency,
            record.vatValue || 0,
            record.purchasePrice,
            record.attrib0,
            record.attrib0_id,
            record.attrib1,
            record.attrib1_id,
            record.attrib2,
            record.attrib2_id,
            record.supplier,
            record.refId,
            record.billingDate,
            "1",
            { type: String(productType) },
          ),
        ),
      );

      await handleResult(
        result,
        t("record_saved_successfully", { ns: "layout_components" }),
      );
    } catch (error: any) {
      ToastQueue.negative(error.message, { timeout: 3000 });
    } finally {
      setShowLoader(false);
    }
  };

  const updateRecord = async (record: any) => {
    try {
      if (isNew && setData) {
        record.unitPriceCurr =
          formatDecimalToString(record.unitPrice) + " " + record.currency;
        record.billingDateFormatted = moment(record.billingDate).format(
          "MM/DD/YYYY",
        );
        const updatedRowsData = rowsData.map((row: any) =>
          row.id === record.id ? record : row,
        );
        //console.log('updatedRowsData', updatedRowsData);
        setRowsData(updatedRowsData);
        setData(updatedRowsData);
        handleRowClose();
        return;
      }
      setShowLoader(true);
      //console.log('update record', record);
      let itemRow = projectProductData.ROWS.find(
        (row: any) => row.ITEM.id === record.id,
      );
      //console.log('itemRow', itemRow);
      setRecordIds(record, selectedItem);
      let result = await itemService.updateProjectItem(
        new UpdateProjectItemRequest(
          store.Server,
          store.SessionId,
          generateProjectProductItem(
            record.folderId,
            record.projectId,
            record.activityId,
            record.partID,
            record.productName,
            record.quantity,
            record.unitPrice,
            record.totalPrice,
            record.totalAmountWithVat || 0,
            record.currency,
            record.vatValue || 0,
            record.purchasePrice,
            record.attrib0,
            record.attrib0_id,
            record.attrib1,
            record.attrib1_id,
            record.attrib2,
            record.attrib2_id,
            record.supplier,
            record.refId,
            record.billingDate,
            itemRow.ITEM,
          ),
        ),
      );

      await handleResult(
        result,
        t("record_updated_successfully", { ns: "layout_components" }),
      );
    } catch (error) {
    } finally {
      setShowLoader(false);
    }
  };

  const deleteRecord = async (record: any) => {
    try {
      if (isNew && setData) {
        //console.log('record', record);
        const updatedRowsData = rowsData.filter(
          (row: any) => row.id !== record.id,
        );
        //console.log('updatedRowsData', updatedRowsData);
        setRowsData(updatedRowsData);
        setData(updatedRowsData);
        handleRowClose();
        return;
      }
      setShowLoader(true);
      //console.log('record', record);

      let result = await itemService.deleteProjectItem(
        new DeleteProjectItemRequest(store.Server, store.SessionId, record.id),
      );
      await handleResult(
        result,
        t("record_deleted_successfully", { ns: "layout_components" }),
      );
    } catch (error) {
      console.log("deleteRecord Error", error);
    } finally {
      setShowLoader(false);
    }
  };

  const handleResult = async (result: any, successMessage: string) => {
    if (result.result === "OK") {
      await loadProjectProductData();
      handleRowClose();
      ToastQueue.positive(successMessage, { timeout: 3000 });
    } else {
      ToastQueue.negative(result.EXCEPTION.reason, { timeout: 3000 });
    }
  };

  const closeDialog = () => {
    setIsOpen(false);
  };

  if (showLoader) {
    return (
      <Flex width="100%" justifyContent={"center"} marginTop={10}>
        <ProgressCircle aria-label="Loading…" isIndeterminate />
      </Flex>
    );
  } else {
    return (
      <View position={"relative"} width={"100%"}>
        {/* Table section */}
        <View position={"relative"} width={"100%"}>
          <Flex direction={"column"} gap={"size-150"} width={"100%"}>
            <Flex
              direction={"row"}
              alignItems={"start"}
              justifyContent={"start"}
              UNSAFE_className={"heading_text_parent"}
            >
              <Content
                position={"relative"}
                UNSAFE_className={styles.heading_text}
                UNSAFE_style={{
                  border:
                    isRequired && rowsData.length === 0
                      ? "1px solid red"
                      : undefined,
                  borderRadius: "5px",
                  padding: "10px",
                  width: "auto",
                }}
              >
                {componentTitle}
              </Content>
              {isRequired && rowsData.length === 0 && (
                <Flex
                  direction={"row"}
                  UNSAFE_style={{
                    color: "red",
                    fontSize: "10px",
                    padding: "15px",
                  }}
                >
                  {componentTitle.toLocaleLowerCase()} required
                </Flex>
              )}
              <Flex
                direction={"row"}
                alignItems={"center"}
                justifyContent={"center"}
                gap={"size-100"}
                UNSAFE_className={styles.icon_add_parent}
              >
                <Content>
                  <Link
                    isQuiet
                    onPress={(e) => {
                      setIsProductSelectionModalOpen(true);
                    }}
                  >
                    <i className="bi bi-plus fs-5">
                      <View UNSAFE_className={styles.icon_add_text}>
                        {t("add", { ns: "layout_components" })}
                      </View>
                    </i>
                  </Link>
                </Content>
              </Flex>
            </Flex>
            <Flex
              direction={"column"}
              width={"100%"}
              UNSAFE_style={tableStyles}
            >
              <Flex height={containerHeight}>
                <TableView
                  UNSAFE_className="tblLayoutComponent"
                  aria-label="Project Product"
                  onSelectionChange={handleRowClick}
                  selectionMode="single"
                  selectedKeys={selectedKeys}
                  selectionStyle="highlight"
                  height={tableHeight}
                  width={"100%"}
                >
                  <TableHeader
                    columns={isMobile ? columnsDataMobile : columnsData}
                  >
                    {(column) => (
                      <Column
                        showDivider
                        key={column.key}
                        width={column?.width}
                      >
                        {column.name}
                      </Column>
                    )}
                  </TableHeader>
                  <TableBody items={rowsData}>
                    {(item) => (
                      <Row>
                        {(columnKey) => (
                          <Cell>{(item as any)[String(columnKey)]}</Cell>
                        )}
                      </Row>
                    )}
                  </TableBody>
                </TableView>
              </Flex>

              {/* Mobile Dialog Container inside table area */}
              {isMobile && (
                <DialogContainer
                  isDismissable
                  onDismiss={() => {
                    setIsOpen(false);
                  }}
                >
                  {isOpen && (
                    <Dialog>
                      <Heading>
                        {" "}
                        {selectedRow.id === "0"
                          ? t("add", { ns: "layout_components" })
                          : t("edit", { ns: "layout_components" })}
                      </Heading>
                      <Content>
                        <ProjectProductAddEdit
                          id={String(selectedItem?.id)}
                          selectedItemType={selectedItem?.type}
                          defaultRowData={defaultRowData}
                          selectedRowData={selectedRow}
                          handleRowClose={handleRowClose}
                          addRecord={addRecord}
                          updateRecord={updateRecord}
                          deleteRecord={deleteRecord}
                          attrib0ColumnName={attrib0ColumnName}
                          attrib1ColumnName={attrib1ColumnName}
                          attrib2ColumnName={attrib2ColumnName}
                          attrib0ColumnVisible={attrib0ColumnVisible}
                          attrib1ColumnVisible={attrib1ColumnVisible}
                          attrib2ColumnVisible={attrib2ColumnVisible}
                          attributesData={activeAttributesData}
                          billingDateColumnName={billingDateColumnName}
                          billingDateColumnVisible={billingDateColumnVisible}
                          componentTitle={componentTitle}
                          quantityColumnName={quantityColumnName}
                          quantityColumnVisible={quantityColumnVisible}
                          serialNumberColumnName={serialNumberColumnName}
                          serialNumberColumnVisible={serialNumberColumnVisible}
                          unitPriceColumnName={unitPriceColumnName}
                          unitPriceColumnVisible={unitPriceColumnVisible}
                          supplierColumnVisible={supplierColumnVisible}
                          closeDialog={closeDialog}
                          styles={styles}
                          onLoadAttributes={handleLoadAttributes}
                        />
                      </Content>
                    </Dialog>
                  )}
                </DialogContainer>
              )}
            </Flex>
          </Flex>
        </View>

        {/* Desktop AddEdit section - OUTSIDE absolute positioning for proper document flow */}
        {!isMobile && showAddEditRow && (
          <Flex
            direction={"column"}
            width={"100%"}
            marginTop={"size-200"}
            UNSAFE_style={{ zIndex: 2 }}
          >
            <ProjectProductAddEdit
              id={String(selectedItem?.id)}
              selectedItemType={selectedItem?.type}
              defaultRowData={defaultRowData}
              selectedRowData={selectedRow}
              handleRowClose={handleRowClose}
              addRecord={addRecord}
              updateRecord={updateRecord}
              deleteRecord={deleteRecord}
              attrib0ColumnName={attrib0ColumnName}
              attrib1ColumnName={attrib1ColumnName}
              attrib2ColumnName={attrib2ColumnName}
              attrib0ColumnVisible={attrib0ColumnVisible}
              attrib1ColumnVisible={attrib1ColumnVisible}
              attrib2ColumnVisible={attrib2ColumnVisible}
              attributesData={activeAttributesData}
              billingDateColumnName={billingDateColumnName}
              billingDateColumnVisible={billingDateColumnVisible}
              componentTitle={componentTitle}
              quantityColumnName={quantityColumnName}
              quantityColumnVisible={quantityColumnVisible}
              serialNumberColumnName={serialNumberColumnName}
              serialNumberColumnVisible={serialNumberColumnVisible}
              unitPriceColumnName={unitPriceColumnName}
              unitPriceColumnVisible={unitPriceColumnVisible}
              supplierColumnVisible={supplierColumnVisible}
              closeDialog={closeDialog}
              styles={styles}
              onLoadAttributes={handleLoadAttributes}
            />
          </Flex>
        )}

        {/* Product Selection Modal */}
        <ProductSelectionModal
          isOpen={isProductSelectionModalOpen}
          onClose={() => setIsProductSelectionModalOpen(false)}
          onSave={handleSaveSelectedProducts}
        />
      </View>
    );
  }
}

export const ProjectProductComponent = LayoutComponent(ProjectProduct);
